import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionClient,
  formatNumber,
  refusalMessage,
  StockAdjustmentReasonClient,
  type AssemblyVersionDTO,
} from "../../api/manufacturing";
import { WarehouseClient, type ProductDTO } from "../../api/product";

/**
 * A product's finished stock as its versions hold it (design §14). A version
 * part is counted in boxes: per warehouse, its units are the least of its
 * boxes, the rest loose boxes ("18 units, 2 loose box 1"). Until cutover the
 * product's inventory (feeds, KPIs, pipeline) is still what the sales system
 * says per SKU, shown beside it.
 */
export function InventoryTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const stock = useQuery({ queryKey: ["manufacturing", "version-stock", draft.id], queryFn: () => client.stock(draft.id) });
  const structure = useQuery({ queryKey: ["manufacturing", "product-structure", draft.id], queryFn: () => client.ofProduct(draft.id) });
  const [counting, setCounting] = useState(false);
  const versions = structure.data?.versions ?? [];
  const rows = stock.data?.versions ?? [];
  const boxes = Math.max(1, ...rows.map((row) => Object.keys(row.boxes).length), ...versions.map((version) => version.box_count ?? 1));
  const totalUnits = rows.reduce((sum, row) => sum + Number(row.units), 0);
  const salesTotal = (stock.data?.sales_system ?? []).reduce((sum, row) => sum + Number(row.quantity), 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-4 py-4 lg:px-6">
      <section>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="text-[13px] font-semibold">{t("inventory.versions.title", "Boxed stock, by version")}</h3>
          <Badge size="sm" variant="neutral">{t("inventory.versions.total", "{{n}} units", { n: formatNumber(totalUnits) })}</Badge>
          {canWrite && versions.length > 0 && (
            <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setCounting(true)}>
              {t("inventory.count", "Count a box")}
            </Button>
          )}
        </div>
        <p className="mb-2 text-[12px] text-ink-muted">
          {t("inventory.versions.hint", "What assembly orders put away and counts corrected, per box. A unit is one of every box; what is over is loose.")}
        </p>
        {stock.isLoading ? (
          <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>
        ) : rows.length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("inventory.versions.none", "Nothing put away or counted yet.")}</p>
        ) : (
          <table className="w-full max-w-4xl text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <th className="py-1 font-medium">{t("assembly.version.code", "Version")}</th>
                <th className="py-1 font-medium">{t("column.warehouse", "Warehouse")}</th>
                <th className="py-1 text-right font-medium">{t("inventory.units", "Units")}</th>
                {Array.from({ length: boxes }, (_, index) => (
                  <th key={index} className="py-1 text-right font-medium">{t("bom.section.box", "Box {{n}}", { n: index + 1 })}</th>
                ))}
                <th className="py-1 text-right font-medium">{t("inventory.loose", "Loose boxes")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${String(row.assembly_version_id)}-${String(row.warehouse_id)}`} className="border-t border-line">
                  <td className="py-1.5 font-semibold">{row.code}</td>
                  <td className="py-1.5">{row.warehouse_code}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{formatNumber(row.units)}</td>
                  {Array.from({ length: boxes }, (_, index) => (
                    <td key={index} className="py-1.5 text-right tabular-nums text-ink-muted">
                      {row.boxes[String(index + 1)] == null ? "" : formatNumber(row.boxes[String(index + 1)]!)}
                    </td>
                  ))}
                  <td className={`py-1.5 text-right tabular-nums ${Number(row.loose_boxes) > 0 ? "text-warn" : "text-ink-faint"}`}>
                    {formatNumber(row.loose_boxes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-[13px] font-semibold">{t("inventory.sales.title", "Sales system (nexera), per SKU")}</h3>
          <Badge size="sm" variant="neutral">{t("inventory.versions.total", "{{n}} units", { n: formatNumber(salesTotal) })}</Badge>
        </div>
        <p className="mb-2 text-[12px] text-ink-muted">
          {t("inventory.sales.hint", "The product's inventory today: what the feeds, KPIs and the pipeline read. It becomes the versions' stock above at cutover.")}
        </p>
        {(stock.data?.sales_system ?? []).length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("inventory.sales.none", "No stock in the sales system.")}</p>
        ) : (
          <table className="w-full max-w-md text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <th className="py-1 font-medium">{t("column.warehouse", "Warehouse")}</th>
                <th className="py-1 text-right font-medium">{t("inventory.quantity", "On hand")}</th>
                <th className="py-1 text-right font-medium">{t("inventory.reserved", "Reserved")}</th>
              </tr>
            </thead>
            <tbody>
              {(stock.data?.sales_system ?? []).map((row) => (
                <tr key={row.warehouse_id} className="border-t border-line">
                  <td className="py-1.5">{row.warehouse_code}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatNumber(row.quantity)}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-muted">{formatNumber(row.reserved_quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {counting && <CountBoxDialog productId={draft.id} versions={versions} onClose={() => setCounting(false)} />}
    </div>
  );
}

/** Sets how many of one box a warehouse has: the difference is an adjustment, with its reason. */
function CountBoxDialog({ productId, versions, onClose }: { productId: number; versions: AssemblyVersionDTO[]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const warehouses = useQuery({ queryKey: ["warehouse", "all"], queryFn: () => new WarehouseClient(http).findAll() });
  const reasons = useQuery({ queryKey: ["stock-adjustment-reason", "all"], queryFn: () => new StockAdjustmentReasonClient(http).findAll() });
  const [versionId, setVersionId] = useState(String(versions[0]?.id ?? ""));
  const [warehouseId, setWarehouseId] = useState("");
  const [box, setBox] = useState("1");
  const [counted, setCounted] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const version = versions.find((one) => String(one.id) === versionId);
  const valid = version != null && warehouseId !== "" && reasonId !== "" && counted.trim() !== "" && Number(counted) >= 0;
  const count = useMutation({
    mutationFn: () =>
      new AssemblyVersionClient(http).countBox(version!.id, {
        warehouse_id: Number(warehouseId),
        box_number: Number(box),
        counted: Number(counted),
        stock_adjustment_reason_id: Number(reasonId),
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "version-stock", productId] });
      toast.success(t("inventory.count.done", "Count saved"));
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("inventory.count", "Count a box")}
      description={t("inventory.count.description", "How many of this box are there: the difference is an adjustment.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={count.isPending} onClick={() => count.mutate()}>
            {t("inventory.count.confirm", "Save the count")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("assembly.version.code", "Version")} htmlFor="count-version">
          <Select id="count-version" value={versionId} options={versions.map((one) => ({ value: String(one.id), label: one.code }))}
            onChange={(event) => { setVersionId(event.target.value); setBox("1"); }} />
        </Field>
        <Field label={t("column.warehouse", "Warehouse")} htmlFor="count-warehouse">
          <Select id="count-warehouse" value={warehouseId} placeholder={t("inventory.count.warehouse", "Where")}
            options={(warehouses.data ?? []).map((one) => ({ value: String(one.id), label: one.code }))}
            onChange={(event) => setWarehouseId(event.target.value)} />
        </Field>
        <Field label={t("assembly.box.number", "Box")} htmlFor="count-box">
          <Select id="count-box" value={box}
            options={Array.from({ length: Math.max(1, version?.box_count ?? 1) }, (_, index) => ({ value: String(index + 1), label: t("bom.section.box", "Box {{n}}", { n: index + 1 }) }))}
            onChange={(event) => setBox(event.target.value)} />
        </Field>
        <Field label={t("inventory.count.counted", "Counted")} htmlFor="count-counted" required>
          <Input id="count-counted" type="number" min="0" step="1" value={counted} onChange={(event) => setCounted(event.target.value)} />
        </Field>
        <Field label={t("inventory.count.reason", "Reason")} htmlFor="count-reason" required>
          <Select id="count-reason" value={reasonId} placeholder={t("inventory.count.reason.pick", "Why")}
            options={(reasons.data ?? []).filter((one) => one.is_active).map((one) => ({ value: String(one.id), label: i18n.language.startsWith("fr") ? one.name_fr : one.name }))}
            onChange={(event) => setReasonId(event.target.value)} />
        </Field>
        <Field label={t("form.field.note", "Note")} htmlFor="count-note">
          <Input id="count-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
